/**
 * 全效（OKR 效能）模块 API 封装。
 * 统一走 /core-api 网关，身份头来自 legacy 登录态（UserSysno / UserComSysno）。
 * 岗位职责等仍通过 JSONP 调旧 IUserWork 接口。
 */
import $ from "jquery";
import base from "@/utils/base";
import basecode from "@/utils/BaseCode";
import coreApiRoot from "@/utils/coreApiRoot";

const root = coreApiRoot() + "/api/v1/okr";

/** 构造请求头：用 legacy 租户/用户 ID 标识当前登录人 */
function headers() {
  // 每次请求清除验收身份缓存键，避免与真实登录态混用
  ["CoreAcceptanceUserId", "CoreAcceptanceTenantId", "CoreAcceptanceRole",
    "CoreAcceptancePosition"].forEach(key => localStorage.removeItem(key));
  return {
    "X-Legacy-Tenant-Id": localStorage.getItem("UserComSysno") || "OKR-DEMO-COMPANY",
    "X-Legacy-User-Id": localStorage.getItem("UserSysno") || "OKR-DEMO-USER"
  };
}

/** 进行中的 session 合并成一次请求，首页和全效不要各打一遍 */
let sessionFlight = null
let sessionFlightKey = ''

/** JSON REST 请求封装，失败时抛出后端 message */
function request(method, path, data) {
  return new Promise((resolve, reject) => $.ajax({
    type: method,
    url: root + path,
    headers: headers(),
    contentType: "application/json; charset=UTF-8",
    dataType: "json",
    data: method === "GET" ? data : JSON.stringify(data || {}),
    success: resolve,
    error(xhr) {
      const body = xhr.responseJSON || {};
      reject(new Error(body.message || "全效服务暂时不可用"));
    }
  }));
}

function jsonp(url, data) {
  // 与 baseajax.normalJsonp 保持一致：参数 Base64 编码后拼进 URL
  const encoded = basecode.base64encode(basecode.utf16to8(JSON.stringify(data || {})));
  return new Promise((resolve, reject) => $.ajax({
    type: "get",
    url: url + "/" + encoded,
    dataType: "jsonp",
    timeout: 15000,
    success: resolve,
    error: (xhr, status) => reject(new Error(status || "JSONP 请求失败"))
  }));
}

/** 下载知识库原始文件（二进制流） */
function blob(path) {
  return fetch(root + path, { method: "GET", headers: headers() }).then(async response => {
    if (response.ok) return response.blob();
    let message = "原始文件暂时无法下载";
    try { message = (await response.json()).message || message; } catch (ignore) {}
    throw new Error(message);
  });
}

/** Boss 资料上传：multipart/form-data，不走 JSON body */
async function multipart(path, formData) {
  const response = await fetch(root + path, { method: "POST", headers: headers(), body: formData });
  if (response.ok) return response.json();
  let message = "资料上传失败";
  try { message = (await response.json()).message || message; } catch (ignore) {}
  throw new Error(message);
}

export default {
  learningProfile(employeeId) { return request("GET", "/learning-profile", employeeId ? { employeeId } : {}); },
  refreshLearningProfile(employeeId) { return request("POST", "/learning-profile/refresh" + (employeeId ? `?employeeId=${employeeId}` : ""), {}); },
  capabilityRadar(employeeId) { return request("GET", "/growth/radar", employeeId ? { employeeId } : {}); },
  growthRecommendations(employeeId) { return request("GET", "/growth/recommendations", employeeId ? { employeeId } : {}); },
  saveCapabilityStandard(data) { return request("POST", "/growth/standards", data); },
  assessCapability(data) { return request("POST", "/growth/assessments", data); },
  saveGrowthRecommendation(data) { return request("POST", "/growth/recommendations", data); },
  startSkillExecution(data) { return request("POST", "/growth/skills/start", data); },
  completeSkillExecution(id, data) { return request("POST", `/growth/skills/${id}/complete`, data); },
  skillExecutions(employeeId) { return request("GET", "/growth/skills", employeeId ? { employeeId } : {}); },
  strategyActionMap() { return request("GET", "/strategy/action-map"); },
  strategyManagement() { return request("GET", "/strategy/management"); },
  generateStrategyDraft(data) { return request("POST", "/strategy/draft", data); },
  // 当前 core-app 已注册同步草稿接口；异步接口待后端任务端点上线后再切回。
  createGovernanceDraftTask(data) { return request("POST", "/strategy/draft", data); },
  getGovernanceDraftTask(taskId) { return request("GET", `/strategy/draft/task/${taskId}`); },
  cancelGovernanceDraftTask(taskId) { return request("POST", `/strategy/draft/task/${taskId}/cancel`); },
  updateStrategyNode(id, data) { return request("PUT", `/strategy/nodes/${id}`, data); },
  publishStrategyNode(id) { return request("POST", `/strategy/nodes/${id}/publish`); },
  deactivateStrategyNode(id) { return request("POST", `/strategy/nodes/${id}/deactivate`); },
  createStrategyNode(data) { return request("POST", "/strategy/nodes", data); },
  createStrategyLink(data) { return request("POST", "/strategy/links", data); },
  createStrategyExecutionBinding(data) { return request("POST", "/strategy/execution-bindings", data); },
  strategyExecutionTrace(employeeId, worklogId) { return request("GET", "/strategy/execution-trace", { employeeId, worklogId }); },
  updateStrategyOkrProgress(id, data) { return request("POST", `/strategy/nodes/${id}/okr-progress`, data); },
  workPoints(cadence, periodStart) { return request("GET", "/strategy/work-points", { cadence, periodStart }); },
  availableTasks() { return request("GET", "/strategy/tasks/available"); },
  projectTask(id) { return request("GET", "/strategy/tasks/" + id); },
  supervisorStrategyWorkbench() { return request("GET", "/strategy/supervisor-workbench"); },
  generateWorkPoints(data) { return request("POST", "/strategy/work-points/generate", data); },
  createWorkPoint(data) { return request("POST", "/strategy/work-points", data); },
  feedbackWorkPoint(id, data) { return request("POST", `/strategy/work-points/${id}/feedback`, data); },
  reviseWorkPoint(id, data) { return request("PUT", `/strategy/work-points/${id}`, data); },
  /** 同步员工档案到全效服务（岗位族、主管/Boss 标记等），首页与效能页入口均会调用 */
  synchronize(profile) {
    const key = JSON.stringify(profile || {})
    if (sessionFlight && sessionFlightKey === key) return sessionFlight
    sessionFlightKey = key
    sessionFlight = request("POST", "/session", profile).finally(() => {
      if (sessionFlightKey === key) {
        sessionFlight = null
        sessionFlightKey = ''
      }
    })
    return sessionFlight
  },
  // —— 员工工作台 ——
  workspace(month) { return request("GET", "/workspace", { month }); },
  template(date, cadence) { return request("GET", "/worklog-template", { date, cadence }); },
  submit(payload) { return request("POST", "/worklogs/submit", payload); }
  ,collaboratorOptions() { return request("GET", "/worklogs/collaborator-options"); }
  ,worklogCollaborators(id) { return request("GET", `/worklogs/${id}/collaborators`); }
  ,leaderboard(month, scope) { return request("GET", "/leaderboard", { month, scope }); }
  ,knowledge(query, options) {
    const opts = options || {}
    const data = { query: query || "", limit: opts.limit || 10 }
    if (opts.offset) data.offset = opts.offset
    return request("GET", "/knowledge", data)
  }
  ,knowledgeDetail(id) { return request("GET", "/knowledge/" + id); }
  ,knowledgeFile(id) { return blob("/knowledge/" + id + "/file"); }
  ,history(month) { return request("GET", "/history", { month }); }
  ,worklogDirectionAssessment(id) { return request("GET", `/worklogs/${id}/direction-assessment`); }
  // —— 主管 ——
  ,supervisorDashboard(month) { return request("GET", "/supervisor/dashboard", { month }); }
  ,supervisorReviews(includeReviewed) { return request("GET", "/supervisor/reviews", { includeReviewed: !!includeReviewed }); }
  ,supervisorReviewDetail(id) { return request("GET", "/supervisor/reviews/" + id); }
  ,submitSupervisorReview(id, payload) { return request("POST", "/supervisor/reviews/" + id, payload); }
  ,supervisorMonitor(month) { return request("GET", "/supervisor/monitor", { month }); }
  ,supervisorDiagnosis(month) { return request("GET", "/supervisor/diagnosis", { month }); }
  ,supervisorWeeklyBrief(weekStart) { return request("GET", "/supervisor/weekly-brief", weekStart ? { weekStart } : {}); }
  ,generateSupervisorWeeklyBrief() { return request("POST", "/supervisor/weekly-brief/generate"); }
  ,ackSupervisorWeeklyBrief(id) { return request("POST", "/supervisor/weekly-brief/" + id + "/ack"); }
  ,conveySupervisorWeeklyBrief(id, items) { return request("POST", "/supervisor/weekly-brief/" + id + "/convey", items ? { items } : {}); }
  ,supervisorCorrections() { return request("GET", "/supervisor/corrections"); }
  ,supervisorCorrectionAction(id, payload) { return request("POST", "/supervisor/corrections/" + id + "/actions", payload); }
  ,bossAbilityCard(employeeId, date) { return request("GET", "/boss/ability-card", { employeeId, date }); }
  ,bossSaveGuidance(payload) { return request("POST", "/boss/guidance", payload); }
  // —— Boss ——
  ,bossDashboard(month) { return request("GET", "/boss/dashboard", { month }); }
  ,bossHomeOverview(date) { return request("GET", "/boss/home-overview", { date }); }
  ,bossLogRankings(date, dimension) { return request("GET", "/boss/log-rankings", { date, dimension }); }
  ,bossDepartmentSales(date, businessUnit) {
    const params = {};
    if (date) params.date = date;
    if (businessUnit) params.businessUnit = businessUnit;
    return request("GET", "/boss/department-sales", params);
  }
  ,bossDepartmentBrand(date, businessUnit) {
    const params = {};
    if (date) params.date = date;
    if (businessUnit) params.businessUnit = businessUnit;
    return request("GET", "/boss/department-brand", params);
  }
  ,bossHeatmap(month) { return request("GET", "/boss/heatmap", { month }); }
  ,bossReport(month, cadence) { return request("GET", "/boss/report", { month, cadence }); }
  ,bossRationality(month) { return request("GET", "/boss/rationality", { month }); }
  ,bossCustomerData(view, creator, me) {
    const params = { view: view || "quantity" };
    if (creator !== undefined && creator !== null && creator !== "") {
      params.creator = creator;
    }
    if (me !== undefined && me !== null && me !== "") {
      params.me = me;
    }
    return request("GET", "/boss/customer-data", params);
  }
  ,bossCustomerList(params) {
    const q = Object.assign({ page: 1, size: 20, sortKey: "create_time", sortDir: "desc" }, params || {});
    return request("GET", "/boss/customer-list", q);
  }
  ,addBossCustomer(payload) { return request("POST", "/boss/customer", payload); }
  ,updateBossCustomer(payload) { return request("PUT", "/boss/customer", payload); }
  ,addBossFollowUp(payload) { return request("POST", "/boss/follow-up", payload); }
  ,bossFollowUps(customerId, contactId) { return request("GET", "/boss/follow-up", { customerId, contactId }); }
  ,uploadBossFollowUpImage(formData) { return multipart("/boss/follow-up/uploads", formData); }
  ,bossCustomerDict(type) { return request("GET", "/boss/dict/" + encodeURIComponent(type)); }
  ,bossOwners() { return request("GET", "/boss/owners"); }
  ,uploadBossKnowledge(formData) { return multipart("/boss/knowledge/uploads", formData); }
  ,transcribeBossVoice(formData) { return multipart("/boss/voice/transcribe", formData); }
  ,optimizeBossVoiceText(text) { return request("POST", "/boss/voice/optimize", { text }); }
  ,bossEmployees() { return request("GET", "/boss/employees"); }
  ,publishBossPerformanceConfig(payload) { return request("POST", "/boss/performance-configs/publish", payload); }
  ,publishBossDutyAdjustment(payload) { return request("POST", "/boss/duty-adjustments/save", payload); }
  ,bossTasks(month) { return request("GET", "/boss/tasks", { month }); }
  ,createBossTask(payload) { return request("POST", "/boss/tasks", payload); }
  ,reviewBossTask(id, payload) { return request("POST", "/boss/tasks/" + id + "/review", payload); }
  ,governancePolicies(params) { return request("GET", "/governance/policies", params || {}); }
  ,createGovernancePolicy(payload) { return request("POST", "/governance/policies", payload); }
  ,publishGovernancePolicy(id) { return request("POST", `/governance/policies/${id}/publish`, {}); }
  ,governanceFeedback(params) { return request("GET", "/governance/feedback", params || {}); }
  ,reviewGovernanceFeedback(id, payload) { return request("POST", `/governance/feedback/${id}/review`, payload); }
  ,governanceControls() { return request("GET", "/governance/controls"); }
  ,updateGovernanceControl(code, payload) { return request("PUT", `/governance/controls/${encodeURIComponent(code)}`, payload); }
  ,governanceLineage(params) { return request("GET", "/governance/lineage", params || {}); }
  ,bossAllWorklogs(date) { return request("GET", "/boss/all-worklogs", { date }); }
  ,syncWecomCheckins(date) { return request("POST", `/boss/wecom-checkins/sync?date=${encodeURIComponent(date)}`); }
  ,bossProjectPortfolio(date) { return request("GET", "/boss/projects/portfolio", date ? { date } : {}); }
  ,saveBossProjectOrder(projectIds) { return request("POST", "/boss/projects/portfolio/order", { projectIds }); }
  ,bossProjectDetail(id, date) { return request("GET", `/boss/projects/${id}`, date ? { date } : {}); }
  ,bossProjectValueChecklist(id) { return request("GET", `/boss/projects/${id}/value-checklist`); }
  ,saveBossProjectValueChecklist(id,payload) { return request("POST", `/boss/projects/${id}/value-checklist`, payload); }
  ,createBossProject(payload) { return request("POST", "/boss/projects", payload); }
  ,addBossProjectMember(id,payload) { return request("POST", `/boss/projects/${id}/members`, payload); }
  ,addBossProjectValue(id,payload) { return request("POST", `/boss/projects/${id}/value-events`, payload); }
  ,bossProjectValueChecklist(id) { return request("GET", `/boss/projects/${id}/value-checklist`); }
  ,saveBossProjectValueChecklist(id,payload) { return request("POST", `/boss/projects/${id}/value-checklist`, payload); }
  ,projectTaskPlans(id) { return request("GET", `/projects/${id}/task-planning`); }
  ,generateProjectTaskPlans(id) { return request("POST", `/projects/${id}/task-planning/generate`, {}); }
  ,reviewProjectTaskPlan(id,proposalId,payload) { return request("POST", `/projects/${id}/task-planning/${proposalId}/review`, payload); }
  ,batchReviewProjectTaskPlans(id,payload) { return request("POST", `/projects/${id}/task-planning/batch-review`, payload); }
  ,assignProjectTaskPlan(id,proposalId,employeeId) { return request("POST", `/projects/${id}/task-planning/${proposalId}/assign`, {employeeId}); }
  ,batchAssignProjectTaskPlans(id,payload) { return request("POST", `/projects/${id}/task-planning/batch-assign`, payload); }
  ,projectTaskCandidates(id) { return request("GET", `/projects/${id}/task-planning/candidates`); }
  ,addBossProjectOutcome(id,payload) { return request("POST", `/boss/projects/${id}/outcomes`, payload); }
  ,reviewBossProjectOutcome(id,outcomeId,payload) { return request("POST", `/boss/projects/${id}/outcomes/${outcomeId}/review`, payload); }
  ,bossProjectAlerts(status) { return request("GET", "/boss/projects/alerts", status ? { status } : {}); }
  ,readBossProjectAlert(id) { return request("POST", `/boss/projects/alerts/${id}/read`, {}); }
  ,addBossProjectMilestone(id,payload) { return request("POST", `/boss/projects/${id}/milestones`, payload); }
  ,addBossProjectRisk(id,payload) { return request("POST", `/boss/projects/${id}/risks`, payload); }
  ,recalculateBossProject(id,date) { return request("POST", `/boss/projects/${id}/effectiveness/recalculate${date ? `?date=${encodeURIComponent(date)}` : ""}`); }
  ,simulateBossPortfolio(payload) { return request("POST", "/boss/projects/portfolio/simulate", payload); }
  // —— 员工任务与通知 ——
  ,tasks() { return request("GET", "/tasks"); }
  ,task(id) { return request("GET", "/tasks/" + id); }
  ,taskAction(id, payload) { return request("POST", "/tasks/" + id + "/actions", payload); }
  // —— 组织架构 ——
  ,organizationTree() { return request("GET", "/organization/tree"); }
  ,organizationBusinessUnits() { return request("GET", "/organization/business-units"); }
  /** 岗位职责页人员/组织信息（core-app 新接口，对应 us_userlogininfo 关联查询） */
  ,dutyProfile(userSysno) { return request("GET", "/duty/profile", { userSysno }); }
  /** 岗位职责详情（core-app 新接口，对应 us_dutyinfo；按 用户编号 > 事业部+部门 > 仅部门 取最匹配一行） */
  ,duty(userSysno) { return request("GET", "/duty/duty", { userSysno }); }
  /** 全员岗位职责列表（boss 视角），返回 list: [{sysno,userName,catg_name,catg_nameF,BusinessUnit,...}] */
  ,dutyList() { return request("GET", "/duty/list"); }
  ,notifications() { return request("GET", "/notifications"); }
  ,readNotification(id) { return request("POST", "/notifications/" + id + "/read"); }
  ,corrections() { return request("GET", "/corrections"); }
  ,correction(id) { return request("GET", "/corrections/" + id); }
  ,correctionAction(id, payload) { return request("POST", "/corrections/" + id + "/actions", payload); }
  // —— 财务量化审核 ——
  ,financeQuantitativePendingCounts() { return request("GET", "/finance/quantitative/pending-counts"); }
  ,financeQuantitativeRecords(positionFamily, reviewed) {
    return request("GET", "/finance/quantitative/records", { positionFamily, reviewed: !!reviewed });
  }
  ,financeQuantitativeReview(id, payload) {
    return request("POST", `/finance/quantitative/records/${id}/review`, payload);
  }
  // —— 财务月度绩效结算 ——
  ,financeSettlementSummary(month) { return request("GET", "/finance/settlements/summary", { month }); }
  ,financeSettlements(month, view) { return request("GET", "/finance/settlements", { month, view: view || "pending" }); }
  ,financeSettlementRun(month) { return request("POST", `/finance/settlements/run?month=${encodeURIComponent(month)}`, {}); }
  ,financeSettlementConfirm(employeeId, month, comment) {
    return request("POST", `/finance/settlements/${employeeId}/confirm?month=${encodeURIComponent(month)}`, { comment: comment || "" });
  }
  /** 岗位工作职责文案，与「我的 - 岗位职责」页 userDuty 字段一致 */
  ,userDuty() {
    const userSysno = localStorage.getItem("UserSysno");
    if (!userSysno) return Promise.resolve("");
    return request("GET", "/duty/duty", { userSysno }).then(res => {
      return res && !res.error ? (res.userDuty || "") : "";
    }).catch(() => "");
  }
};



// WEBPACK FOOTER //
// ./src/utils/coreapi.js